/**
 * KBHelpBox Component
 * Displays KB article previews for a specific frame with modal for full content
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import { useKBArticles } from '../../hooks/useKBArticles';

// Default preview length, can be overridden
const DEFAULT_PREVIEW_LENGTH = 100;

// Get preview length from localStorage or use default
const getPreviewLength = (): number => {
  const stored = localStorage.getItem('kb-preview-length');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if ([50, 100, 150, 200].includes(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_PREVIEW_LENGTH;
};

interface KBHelpBoxProps {
  frameId: string;
  className?: string;
}

interface KBArticle {
  id: string;
  title: string;
  content: string;
  slug?: string;
  category?: string;
  categoryName?: string;
}

const KBHelpBox: React.FC<KBHelpBoxProps> = ({ frameId, className = '' }) => {
  const { articles, loading } = useKBArticles(frameId);
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);
  const [showModal, setShowModal] = useState(false);
  const previewLength = getPreviewLength();

  // Don't render anything if no articles or still loading
  if (loading || !articles || articles.length === 0) {
    return null;
  }

  const getPreviewText = (content: string): string => {
    // Remove markdown formatting for preview
    const plainText = content
      .replace(/#{1,6}\s/g, '') // Remove headers
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
      .replace(/`([^`]+)`/g, '$1') // Remove inline code
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/>\s/g, '') // Remove blockquotes
      .replace(/[-*]\s/g, '') // Remove list markers
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();

    if (plainText.length <= previewLength) {
      return plainText;
    }
    return plainText.substring(0, previewLength).trim() + '...';
  };

  const openModal = (article: KBArticle) => {
    setSelectedArticle(article);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };

  // Get content from article - handle both old format (content.external) and new format (content as string)
  const getArticleContent = (article: unknown): string => {
    if (typeof article.content === 'string') {
      return article.content;
    }
    if (article.content?.external) {
      return article.content.external;
    }
    return '';
  };

  return (
    <>
      {/* KB Preview Cards */}
      <div className={`space-y-3 ${className}`}>
        {articles.map((article: unknown) => {
          const content = getArticleContent(article);
          return (
            <button
              key={article.id}
              onClick={() => openModal({ ...article, content })}
              className="w-full text-left p-4 bg-gradient-to-r from-teal-50 to-cyan-50 
                         border border-teal-200 rounded-lg hover:border-teal-400 
                         hover:shadow-md transition-all duration-200 group"
            >
              <h4 className="font-medium text-teal-800 group-hover:text-teal-900 
                            text-sm sm:text-base mb-1 line-clamp-2">
                {article.title}
              </h4>
              <p className="text-teal-600 text-xs sm:text-sm line-clamp-2">
                {getPreviewText(content)}
              </p>
              <span className="text-teal-500 text-xs mt-2 inline-block group-hover:text-teal-700">
                Click to read more →
              </span>
            </button>
          );
        })}
      </div>

      {/* Full Article Modal */}
      {showModal && selectedArticle && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4 flex-1">
                {selectedArticle.title}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 
                           rounded-lg transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="text-gray-700 [&_h1]:text-gray-900 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-gray-900 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-gray-900 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:my-1 [&_a]:text-teal-600 [&_a]:underline [&_strong]:font-bold [&_strong]:text-gray-900 [&_em]:italic [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_blockquote]:border-l-4 [&_blockquote]:border-teal-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-gray-50 [&_blockquote]:py-2 [&_hr]:my-4 [&_hr]:border-gray-300">
                <ReactMarkdown 
                  remarkPlugins={[remarkBreaks]}
                  rehypePlugins={[rehypeRaw]}
                >{selectedArticle.content}</ReactMarkdown>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={closeModal}
                className="w-full sm:w-auto px-6 py-2.5 bg-teal-600 text-white rounded-lg 
                           hover:bg-teal-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KBHelpBox;

